import { computed, defineComponent, PropType, toRaw } from "vue";
import { Container } from "aurelia-dependency-injection";
import $ from "jquery";
import moment from "moment";
import GoogleMaps = google.maps;

import eventBus from "../../../../../utilities/eventBus";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";
import { AgencyService } from "../../../../../services/agency.service";
import {
    ApplicationEnum,
    ParentPermissionEnum,
    PermissionActionEnum,
    EvvStatusHomeCareEnum,
    EvvStatusHomeHealthEnum
} from "../../../../../enums/enums";
import { ProductService } from "../../../../../services/product.service";
import { IVisitMapDetails } from "../../../../../resources-vue/vue-interfaces/i-visit-map";
import { ILocation } from "../../../../../resources-vue/vue-interfaces/i-location";
import { CalendarMapService } from "../../../../../services-vue/calendar-map-service";
import {
    GeolocationService,
    GeoLocationEvents,
    ensureLocationData,
    hasFiniteCoords,
    requestDeviceLocation
} from "../../../../../services-vue/geolocation.service";
import { IAddCalendarMarkerOptions } from "../../../../../resources-vue/vue-interfaces/i-calendar-map";
import { nonEvvVisitStatus } from "../DailyCalendarContent/DailyCalendarContent";
import {
    formatFullAddress,
    formatScheduledTime,
    formatTimeInOut
} from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { TaskService } from "../../../../../services/task.service";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import {
    getProductPillClass,
    getProductFullName
} from "../../../../../common/vue-helpers/modifiers/application-modifiers";
import { GetZipEvents } from "../../../../../resources-vue/vue-dialogs/GetZip/GetZip";
import { getSVGIcon } from "../../../VisitMapSVGs";

import ReassignVisit from "../../../../../resources-vue/vue-dialogs/ReassignVisit/ReassignVisit.vue";
import VisitDetails from "../../../../../resources-vue/vue-dialogs/VisitDetails/VisitDetails.vue";
import GetZip from "../../../../../resources-vue/vue-dialogs/GetZip/GetZip.vue";

export default defineComponent({
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    props: {
        mapVisitsList: { type: Array as PropType<IVisitMapDetails[]>, default: [] },
        date: { type: String, default: moment().format("MM/DD/YYY") }
    },

    emits: ["mapRefresh"],

    data() {
        return {
            _myMarker: null as GoogleMaps.Marker,
            _bounds: null as GoogleMaps.LatLngBounds,
            _map: null as GoogleMaps.Map,
            _agencyService: null as AgencyService,
            _prodService: null as ProductService,
            _analyticsService: null as AnalyticsService,
            _calendarMapService: null as CalendarMapService,
            _taskService: null as TaskService,
            _permissionManager: null as PermissionManager,
            _geolocationService: null as GeolocationService,
            _locationData: {} as ILocation,
            _createdMarkerCount: 0,
            _totalLocations: 0,
            locationsFromCoords: {} as any,
            locationsFromAddress: {} as any,
            currentInfoWindow: null,
            allLocationCoords: [],
            highlightEmployeesList: [],
            dialogRef: null,
            isReassignAllowed: false as boolean,
            zipNotEntered: false as boolean
        };
    },

    async created() {
        this._agencyService = Container.instance.get(AgencyService);
        this._prodService = Container.instance.get(ProductService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this._calendarMapService = Container.instance.get(CalendarMapService);
        this._taskService = Container.instance.get(TaskService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._geolocationService = Container.instance.get(GeolocationService);
        this.initSubscriptions();
        await this._init();
        eventBus.on("highlightEmployeeMarker", (selectedEmployees: any) => {
            this.highlightEmployeesList = selectedEmployees.map((emp: any) => emp.name);
            this._clearLocationMarkers(this.locationsFromCoords);
            this._clearLocationMarkers(this.locationsFromAddress);
            this.allLocationCoords = [];
            this._markLocations(this.locationsFromCoords);
            this._markLocations(this.locationsFromAddress);
        });
    },

    watch: {
        mapVisitsList: {
            handler() {
                this._clearAllLocations(this.locationsFromCoords);
                this._clearAllLocations(this.locationsFromAddress);
                this.highlightEmployeesList = [];
                this._init();
            },
            deep: true
        }
    },

    async mounted() {
        this.isReassignAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.clinician,
            PermissionActionEnum.canReassign
        );
    },

    methods: {
        initSubscriptions() {
            eventBus.on(GeoLocationEvents.Retrieved, async () => {
                this._clearAllLocations(this.locationsFromCoords);
                this._clearAllLocations(this.locationsFromAddress);
                await this._init();
            });

            eventBus.on(GetZipEvents.Changed, () => {
                this.zipNotEntered = false;
            });

            eventBus.on(GetZipEvents.Canceled, (isChange: boolean) => {
                if (!isChange) {
                    this.zipNotEntered = true;
                }
            });
        },

        async enterLocation() {
            await this.openGetZipDialog();
        },

        async openGetZipDialog(zip: string = "") {
            let zipOptions = {
                zip: zip
            };
            this.dialogRef = this.$dialog.open(GetZip, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    zipOptions
                }
            });
        },

        async _init() {
            // Without location data, the map center is NaN and Google Maps throws InvalidValueError.
            if (!ensureLocationData(this._geolocationService, this._locationData)) {
                // Try the device's current location automatically before asking the user for a zip.
                requestDeviceLocation(this._geolocationService, () => {
                    this.zipNotEntered = true;
                });
                return;
            }
            this.zipNotEntered = false;
            await this._calendarMapService.loadMapsApi();
            this._markUser();
            if (this.mapVisitsList.length > 0) {
                this.allLocationCoords = [];
                await this._groupVisitsByLocation();
                this._markLocations(this.locationsFromCoords);
            }
        },

        _markUser(): void {
            try {
                let lat: number = +this._locationData?.lat;
                let lon: number = +this._locationData?.lon;
                if (!hasFiniteCoords(lat, lon)) {
                    console.warn("EmployeeVisitMap: Skipping map init, no valid location coordinates available.");
                    return;
                }
                this._bounds = this._calendarMapService.getBounds();
                this._map = this._calendarMapService.getMap(document.getElementById("Employee__VisitMap"), {
                    center: {
                        lat: lat,
                        lng: lon
                    },
                    zoom: 15, // Higher is zoom in and lower is zoom out
                    zoomControl: true,
                    mapTypeControl: false,
                    scaleControl: false,
                    streetViewControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                    gestureHandling: "greedy"
                });
                this._myMarker = this._calendarMapService.addMarker({
                    position: {
                        lat: lat,
                        lng: lon
                    },
                    map: this._map,
                    animationType: "drop",
                    type: "user"
                });
                let info = this._calendarMapService.addInfoWindow({
                    content: "<div class='text-center fw-normal py-1'>Your Location</div>"
                });
                // Marker Events
                this._myMarker.addListener("mouseover", () => {
                    info.open(this._map, this._myMarker);
                });
                this._myMarker.addListener("mouseout", () => {
                    info.close();
                });

                this._bounds.extend(this._myMarker.getPosition());
            } catch (error) {
                console.error(error?.message);
            }
        },

        getUpdatedLatLng(lat: any, lng: any): any {
            let latLngStr = lat + "," + lng;
            if (!this.allLocationCoords.includes(latLngStr)) {
                return { lat, lng };
            }
            return this.getUpdatedLatLng(lat, lng + 0.0001);
        },

        updateLocationsList(locations: any, coords: any, empName: string, visit: any) {
            let latLngStr = coords.lat + "," + coords.lng;
            if (Object.keys(locations).includes(empName)) {
                if (Object.keys(locations[empName]).includes(latLngStr)) {
                    locations[empName][latLngStr].push(visit);
                } else {
                    locations[empName][latLngStr] = [visit];
                    this._totalLocations++;
                }
            } else {
                locations[empName] = { [latLngStr]: [visit] };
                this._totalLocations++;
            }
        },

        async _groupVisitsByLocation(): Promise<void> {
            for (let visit of this.mapVisitsList) {
                let currentEmpName =
                    visit.EmployeeName?.FirstName + " " + visit.EmployeeName?.LastName + " " + visit.Application;
                currentEmpName = currentEmpName != " " ? currentEmpName : "Unassigned";
                let coords = {
                    lat: visit.PatientAddress.AddressLatitude,
                    lng: visit.PatientAddress.AddressLongitude
                };

                if (coords.lat && coords.lng) {
                    this.updateLocationsList(this.locationsFromCoords, coords, currentEmpName, visit);
                } else {
                    const address = formatFullAddress(visit.PatientAddress);
                    if (address && address.length > 3) {
                        await this._calendarMapService.getCoordsFromAddress(address, (newcoords) => {
                            this.updateLocationsList(this.locationsFromAddress, newcoords, currentEmpName, visit);
                            this._clearLocationMarkers(this.locationsFromAddress);
                            this._markLocations(this.locationsFromAddress);
                        });
                    }
                }
            }
        },

        _markLocations(locations: any): void {
            if (this._map && locations && !$.isEmptyObject(locations)) {
                for (let empName of Object.keys(locations)) {
                    for (let locs of Object.keys(locations[empName])) {
                        let empVisits = locations[empName][locs];
                        const image = this.getMarkerImage(empVisits[0]);
                        let highlightPin: boolean = this.highlightEmployeesList.includes(empName);
                        let icon = highlightPin
                            ? { url: image, scaledSize: new google.maps.Size(60, 86) }
                            : { url: image };

                        let [lat, lng] = locs.split(",");

                        let coords = {
                            lat: Number(lat),
                            lng: Number(lng)
                        };
                        let marker = null;
                        let infoWindow = null;

                        let content = this.getEmployeeInfoWindowContent(empVisits);
                        if (!!coords.lat && !!coords.lng) {
                            try {
                                if (this.allLocationCoords.includes(coords.lat + "," + coords.lng)) {
                                    // To display multiple markers with same coords, modifying marker coords by small value.
                                    const res = this.getUpdatedLatLng(coords.lat, coords.lng + 0.0001);
                                    coords = {
                                        lat: res.lat,
                                        lng: res.lng
                                    };
                                }
                                let options: IAddCalendarMarkerOptions = {
                                    position: coords,
                                    map: this._map,
                                    animationType: "drop",
                                    icon: icon
                                };
                                this._bounds.extend(coords);
                                marker = this._calendarMapService.addMarker(options);
                                infoWindow = this._calendarMapService.createInfoWindow(content);
                                // keeps the track of all the marked locations
                                this.allLocationCoords.push(coords.lat + "," + coords.lng);
                                this._analyticsService.logEvent({
                                    category: "Create-Marker",
                                    action: "Coordinates"
                                });
                            } catch (error) {
                                console.error(error);
                            }
                        }

                        locations[empName][locs].marker = marker;
                        this.initMarkerEvents(marker, infoWindow, locations[empName][locs]);
                    }
                }
            }
        },

        getMarkerImage(visit: any) {
            const name = visit.EmployeeName;
            const empName = name?.FirstName ? name?.FirstName + "" + name?.LastName : null;
            const credential = visit?.EmployeeCredential?.split(",")[0];
            let image = "";
            if (empName) {
                if (!credential) {
                    image = "None";
                } else {
                    image = credential;
                }
            } else {
                image = "Unassigned";
            }
            return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(getSVGIcon(image));
        },

        initMarkerEvents(markerInstance: GoogleMaps.Marker, infoWindow: any, visits: any) {
            if (markerInstance) {
                markerInstance.addListener("click", () => {
                    if (this.currentInfoWindow != null) {
                        this.currentInfoWindow.close();
                        this._analyticsService.logEvent({
                            category: AnalyticsCategory.CentralCalendarNavigation,
                            action: "Central Employee calendar Map visit info Close"
                        });
                    }
                    infoWindow.open(this._map, markerInstance);
                    this.currentInfoWindow = infoWindow;
                    this._analyticsService.logEvent({
                        category: AnalyticsCategory.CentralCalendarNavigation,
                        action: "Central Employee calendar Map visit info Open"
                    });
                });

                // storing the reference, as reference changes in google map events
                const thisReference = this;

                google.maps.event.addListener(infoWindow, "domready", (x) => {
                    document.querySelectorAll(".dropdown-reassign").forEach((el) => {
                        el.addEventListener("click", () => {
                            let visitId = el.id.split("_")[2];
                            let currentVisitDetails = visits.find((v: any) => v.Id === visitId);
                            thisReference.scheduledVisit(currentVisitDetails);
                            this._analyticsService.logEvent({
                                category: AnalyticsCategory.CentralCalendarNavigation,
                                action: "Central Patient calendar Map VisitReassign"
                            });
                        });
                    });
                    document.querySelectorAll(".dropdown-details").forEach((el) => {
                        el.addEventListener("click", () => {
                            let visitId = el.id.split("_")[2];
                            let currentVisitDetails = visits.find((v: any) => v.Id === visitId);
                            thisReference.showVisitDetails(currentVisitDetails);
                            this._analyticsService.logEvent({
                                category: AnalyticsCategory.CentralCalendarNavigation,
                                action: "Central Patient calendar Map VisitDetails"
                            });
                        });
                    });
                });
                this._createdMarkerCount++;
                if (this._createdMarkerCount === this._totalLocations) {
                    this._map.fitBounds(this._bounds);
                }
            }
        },

        _clearLocationMarkers(locations: any) {
            for (let empName of Object.keys(locations)) {
                for (let locs of Object.keys(locations[empName])) {
                    let currentMarker = locations[empName][locs].marker;
                    if (currentMarker) {
                        toRaw(currentMarker).setMap(null);
                    }
                }
            }
        },

        _clearAllLocations(locations: any): void {
            for (let empName of Object.keys(locations)) {
                for (let locs of Object.keys(locations[empName])) {
                    let currentMarker = locations[empName][locs].marker;
                    if (currentMarker) {
                        toRaw(currentMarker).setMap(null);
                    }
                }
                delete locations[empName];
            }
            this._totalLocations = 0;
            this._createdMarkerCount = 0;
        },

        getEmployeeInfoWindowContent(visits: any): string {
            let inProgressVisits: string = "";
            let completedVisits: string = "";
            let notYetStartedVisits: string = "";
            let status = "";
            for (let v of visits) {
                status = v.Status;
                if (nonEvvVisitStatus.notYetStarted.includes(status.toLowerCase())) {
                    notYetStartedVisits += this.getVisitDetails(v, true);
                } else if (nonEvvVisitStatus.inProgress.includes(status.toLowerCase())) {
                    inProgressVisits += this.getVisitDetails(v, false);
                } else {
                    completedVisits += this.getVisitDetails(v, false);
                }
            }

            let name = visits[0].EmployeeName;
            let empName = name?.FirstName && name?.LastName ? `${name.FirstName} ${name.LastName}` : "Unassigned";
            let tempApplicationId: ApplicationEnum;
            if (visits[0].Application === 256) {
                tempApplicationId = ApplicationEnum.AxxessHospice;
            } else {
                tempApplicationId = visits[0].Application;
            }

            return ` <div id="${visits[0].UserId}" class="visit-marker-details">
                    <span class="${getProductPillClass(tempApplicationId)} pill">
                        ${getProductFullName(tempApplicationId)}
                    </span>
                <big class="info-header"><b>${empName}${
                    empName == "Unassigned" ? "" : ", " + visits[0].EmployeeCredential
                }</b></big>

                ${notYetStartedVisits !== "" ? `<div class="visit-type">${status}</div>${notYetStartedVisits}` : ""}
                ${inProgressVisits !== "" ? `<div class="visit-type">In Progress</div>${inProgressVisits}` : ""}
                ${completedVisits !== "" ? `<div class="visit-type">Completed</div>${completedVisits}` : ""}
                <div class="visit-address">${formatFullAddress(visits[0].PatientAddress)}</div>
                </div>`;
        },

        getVisitDetails(visit: any, showReassignBtn: boolean = false) {
            let visitTime = formatTimeInOut(
                formatScheduledTime(visit.StartDateTime),
                formatScheduledTime(visit.EndDateTime)
            );
            let patientName = visit.PatientName.FirstName + " " + visit.PatientName.LastName;

            let evvHouse = null;
            if (visit.Application == ApplicationEnum.HomeCare) {
                if (
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.Finished ||
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                ) {
                    evvHouse =
                        visit.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                            ? `<a class='evv'><i class='evv-adjusted signed'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgress ||
                    visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                ) {
                    evvHouse =
                        visit.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                            ? `<a class='evv'><i class='evv-adjusted in-progress'></i></a>`
                            : `<a class='evv'><i class='fa fa-home fa-md'></i></a>`;
                } else if (visit.EvvIconStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            } else if (visit.Application == ApplicationEnum.AgencyCore) {
                if (visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.InProgress) {
                    evvHouse = `<a class='evv ps-1'><i class='fa fa-home fa-md'></i></a>`;
                } else if (
                    visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.PatientVerified ||
                    visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.ClinicianVerified
                ) {
                    evvHouse = `<a class='evv'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (visit.VerificationVisitStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            }

            return `<div class="visit-details">
                <div class="fw-normal">${visit.VisitName}</div>
                <div>${visitTime}</div>
                <div>Scheduled to patient ${patientName}</div>
                 <div class="evvHouse">${evvHouse ? evvHouse : ""}</div>
                <span class="table-btn-group dropdown ms-auto btn-reassign" id = "edit-icon-${visit.Id}">
                <button type="button" class="border-0 btn px-1 py-0 btn-outline-info dropdown-toggle" data-bs-toggle="dropdown" data-bs-display="static" id = "edit-btn-${
                    visit.Id
                }">
                    <i class="fas fa-edit"></i>
                </button>
                <div class="dropdown-menu">
                 ${
                     this.isReassignAllowed && showReassignBtn
                         ? `<a
                            class="dropdown-item dropdown-reassign"
                            href="javascript:void(0);"
                            id = "btn_reassign_${visit.Id}"
                        >
                        Reassign
                        </a>`
                         : ""
                 }
                    <a class="dropdown-item dropdown-details" href="javascript:void(0);" id = "btn_details_${visit.Id}">
                        Details
                    </a>
                </div>
            </span>
                </div>`;
        },

        async scheduledVisit(visitDetails: any) {
            visitDetails.TaskName = visitDetails.VisitName;
            visitDetails.UserFirstName = visitDetails.EmployeeName.FirstName;
            visitDetails.UserLastName = visitDetails.EmployeeName.LastName;
            visitDetails.FirstName = visitDetails.PatientName.FirstName;
            visitDetails.LastName = visitDetails.PatientName.LastName;

            this.dialogRef = this.$dialog.open(ReassignVisit, {
                props: {
                    modal: true,
                    showHeader: false,
                    contentStyle: {
                        minWidth: "650px",
                        minHeight: visitDetails.Application === ApplicationEnum.HomeCare ? "350px" : "280px"
                    }
                },
                data: {
                    visitDetails
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this.$emit("mapRefresh");
                    }
                }
            });
        },

        async showVisitDetails(visitDetails: any) {
            visitDetails.TaskName = visitDetails.VisitName;
            visitDetails.UserFirstName = visitDetails.EmployeeName.FirstName;
            visitDetails.UserLastName = visitDetails.EmployeeName.LastName;
            visitDetails.FirstName = visitDetails.PatientName.FirstName;
            visitDetails.LastName = visitDetails.PatientName.LastName;

            this.dialogRef = this.$dialog.open(VisitDetails, {
                props: { modal: true, showHeader: false },
                data: {
                    visitDetails
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this.$emit("mapRefresh");
                    }
                }
            });
        }
    }
});
